import axios from 'axios';
import type { AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';

import { AbortError } from '@/overmind/errors';

export type Params = Record<string, string>;

export type ApiConfig = AxiosRequestConfig;

export type AuthorizationType = 'Bearer' | 'Param' | 'none';

export type ApiBase = {
  setToken: (newToken: string | undefined) => void;
  setBasePath: (basePath: string) => void;
  initialize: (config: ApiConfig) => void;
};

export type Api = {
  setToken: (newToken: string | undefined) => void;
  setBasePath: (basePath: string) => void;
  get: <T>(
    path: string,
    params?: Params,
    config?: Omit<AxiosRequestConfig, 'params' | 'url'>
  ) => Promise<T>;
  post: <T>(
    path: string,
    body: any,
    config?: Omit<AxiosRequestConfig, 'data' | 'url'>
  ) => Promise<T>;
  patch: <T>(
    path: string,
    body: any,
    config?: Omit<AxiosRequestConfig, 'data' | 'url'>
  ) => Promise<T>;
  put: <T>(
    path: string,
    body: any,
    config?: Omit<AxiosRequestConfig, 'data' | 'url'>
  ) => Promise<T>;
  delete: <T>(
    path: string,
    params?: Params,
    config?: Omit<AxiosRequestConfig, 'params' | 'url'>
  ) => Promise<T>;
  request: <T>(config: AxiosRequestConfig) => Promise<T>;
};

const handleError = (error: AxiosError) => {
  if (axios.isCancel(error)) {
    throw new AbortError();
  }

  throw error;
};

const handleResponse = (response: AxiosResponse) => response.data;

export default (config: ApiConfig, authType: AuthorizationType) => {
  let token: string | undefined;

  const axiosInstance = axios.create(config);

  const authorizationStrategy = getAuthorizationStrategy(authType);
  const api: Api = {
    setToken(newToken) {
      token = newToken;
    },
    setBasePath(basePath: string) {
      axiosInstance.defaults.baseURL = basePath;
    },
    get(path, params, config) {
      return axiosInstance
        .get(path, authorizationStrategy(token, config, params))
        .then(handleResponse)
        .catch(handleError);
    },
    post(path, body, config) {
      return axiosInstance
        .post(path, body, authorizationStrategy(token, config))
        .then(handleResponse)
        .catch(handleError);
    },
    patch(path, body, config) {
      return axiosInstance
        .patch(path, body, authorizationStrategy(token, config))
        .then(handleResponse)
        .catch(handleError);
    },
    put(path, body, config) {
      return axiosInstance
        .put(path, body, authorizationStrategy(token, config))
        .then(handleResponse)
        .catch(handleError);
    },
    delete(path, params, config) {
      return axiosInstance
        .delete(path, authorizationStrategy(token, config, params))
        .then(handleResponse)
        .catch(handleError);
    },
    request(config) {
      return axiosInstance
        .request(authorizationStrategy(token, config))
        .then(handleResponse)
        .catch(handleError);
    }
  };

  return api;
};

function getAuthorizationStrategy(authType: string) {
  const withAuthorizationHeader = (
    token: string | undefined,
    config: ApiConfig = {},
    params: Record<string, string | number | boolean> = {}
  ) => ({
    ...config,
    params,
    headers: {
      ...(config.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  const withTokenParam = (
    token: string | undefined,
    config: ApiConfig = {},
    params: Record<string, string | number | boolean> = {}
  ) => ({
    ...config,
    params: {
      ...(config.params || {}),
      ...params,
      ...(token ? { token } : {})
    }
  });

  const withoutAuth = (
    _token: string | undefined,
    config: ApiConfig = {},
    params: Record<string, string | number | boolean> = {}
  ) => ({
    ...config,
    params: {
      ...(config.params || {}),
      ...params
    }
  });

  if (authType === 'Bearer') {
    return withAuthorizationHeader;
  } else if (authType === 'Param') {
    return withTokenParam;
  }
  return withoutAuth;
}
